// src/annihilation/SkillBar.jsx
// ============================================================
//  ANNIHILATION — the seven-slot skill bar.
//  Source of truth: beings.js SKILL_SLOTS (the shared bar
//  vocabulary), skilltrees/trees.js (slot-unlock node map).
//
//  The bar is a visible progress bar of the character's whole
//  journey — an Escling sees seven slots and six of them dark.
//  Everything here DERIVES: tier gates from level, node gates
//  from the spent map, ability names from the being registry.
//  This component stores nothing and decides no legality.
//
//  Slots 1–4 (Lyt / Primera / Impera / U'hlti) are tier-gated.
//  Slots 5–7 (Transformation / King's Grace / Emperium) are
//  node-gated — owned unlock nodes light them.
//
//  Used first as the Trainer-room preview; PlayerController
//  (Batch 5) mounts this same component in the field HUD.
// ============================================================

import React from "react";
import { SKILL_SLOTS, getBeing } from "./beings";
import { levelForXp, tierForLevel, TIERS } from "./stats";
import {
  SLOT_UNLOCK_NODES,
  UNIVERSAL_UNLOCK_NODES,
  normalizeTreeState,
} from "./trees";

const TIER_ORDER = [TIERS.BEB, TIERS.CHAMPION, TIERS.LORD];
const tierMet = (current, required) =>
  TIER_ORDER.indexOf(current) >= TIER_ORDER.indexOf(required);

export default function SkillBar({ character, compact = false }) {
  const being = getBeing(character?.beingId);
  if (!being) return null;

  const { level } = levelForXp(character?.xp || 0);
  const tier = tierForLevel(level);
  const state = normalizeTreeState(character?.skillTrees);

  const line =
    (being.lines || []).find((l) => l.id === character?.equippedLineId) ||
    (being.lines || [])[0] ||
    null;

  const unlockMap = SLOT_UNLOCK_NODES[being.id] || null;

  // Slot fill — { name, lit } per slot, all derived.
  const fills = SKILL_SLOTS.map((slot) => {
    switch (slot.id) {
      case "lyt":
        return { slot, name: being.lytAttack?.name, lit: true };
      case "primera":
        return {
          slot,
          name: line?.primera?.name,
          lit: tierMet(tier, TIERS.BEB),
        };
      case "impera":
        return {
          slot,
          name: line?.impera?.name,
          lit: tierMet(tier, TIERS.CHAMPION),
        };
      case "uhlti":
        return {
          slot,
          name: line?.uhlti?.name,
          lit: tierMet(tier, TIERS.LORD),
        };
      case "transformation":
        return {
          slot,
          name: being.transformation?.name,
          lit: Boolean(unlockMap && state.spent[unlockMap.transformation]),
        };
      case "kings_grace":
        return {
          slot,
          name: being.kingsGrace?.name,
          lit: Boolean(unlockMap && state.spent[unlockMap.kings_grace]),
        };
      case "emperium":
        return {
          slot,
          name: being.emperium?.name,
          lit: Boolean(unlockMap && state.spent[unlockMap.emperium]),
        };
      default:
        return { slot, name: null, lit: false };
    }
  });

  const universals = Object.entries(UNIVERSAL_UNLOCK_NODES)
    .filter(([, nodeId]) => state.spent[nodeId])
    .map(([key]) => key);

  return (
    <div style={S.wrap}>
      <div style={S.bar}>
        {fills.map(({ slot, name, lit }) => (
          <div
            key={slot.id}
            style={{ ...S.slot, ...(lit ? S.slotLit : S.slotDark) }}
            title={
              lit
                ? `${slot.name}: ${name || "—"}`
                : `${slot.name} — not yet earned`
            }
          >
            <div style={S.slotNum}>{slot.slot}</div>
            <div style={{ ...S.slotName, ...(lit ? null : S.slotNameDark) }}>
              {lit ? name || slot.name : "—"}
            </div>
            {!compact ? <div style={S.slotKind}>{slot.name}</div> : null}
          </div>
        ))}
      </div>
      {universals.length ? (
        <div style={S.uniRow}>
          {universals.map((u) => (
            <span key={u} style={S.uniChip}>
              {u.charAt(0).toUpperCase() + u.slice(1)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const gold = "#e8a33d";
const S = {
  wrap: { width: "100%" },
  bar: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 5,
  },
  slot: {
    borderRadius: 8,
    padding: "7px 3px 6px",
    textAlign: "center",
    minWidth: 0,
  },
  slotLit: {
    border: "1px solid rgba(232,163,61,0.65)",
    background: "rgba(232,163,61,0.1)",
    boxShadow: "0 0 12px rgba(232,163,61,0.12)",
  },
  slotDark: {
    border: "1px solid rgba(138,122,92,0.25)",
    background: "rgba(0,0,0,0.25)",
  },
  slotNum: { color: "#6d5f47", fontSize: 9, marginBottom: 3 },
  slotName: {
    color: gold,
    fontSize: 9.5,
    lineHeight: 1.25,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  slotNameDark: { color: "#4d4436" },
  slotKind: {
    color: "#8a7a5c",
    fontSize: 7.5,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginTop: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  uniRow: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    marginTop: 6,
  },
  uniChip: {
    color: "#8fd18a",
    fontSize: 9.5,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    border: "1px solid rgba(143,209,138,0.4)",
    borderRadius: 999,
    padding: "2px 10px",
  },
};
